// needs-decision-option-id-label-pairs.ts — V2-1 (spec V2 §4.1 "Mở rộng
// schema result", E3).
//
// Đọc `options[]` free-form của result `needs_decision` thành NHÃN (luật v1,
// không đổi) và — khi worker khai đủ — cặp `id → nhãn`. Cặp chỉ hợp lệ khi
// MỌI option có `id` khớp `^[a-z0-9-]{1,32}$` và các id duy nhất; thiếu, trùng
// hay sai dạng một id ⇒ `pairs` vắng ⇒ hành vi v1 (hỏi theo nhãn) và KHÔNG
// BAO GIỜ auto. Không có đường "sửa hộ" id của worker: id là thứ người và
// controller trích lại, đoán nghĩa id hỏng là chọn hộ người.

/** Dạng id option của output contract V2 (spec §4.1). */
export const OPTION_ID_PATTERN = /^[a-z0-9-]{1,32}$/;

export interface OptionIdLabelPair {
  id: string;
  label: string;
  /** Option nguyên bản của worker — luật auto quét mọi field string của nó (§4.1 điều 3). */
  raw: Record<string, unknown>;
}

export interface NeedsDecisionOptions {
  /** Nhãn theo luật v1 (đã bỏ trùng) — đầu vào DUY NHẤT của hash id câu hỏi (phase 01). */
  v1Labels: string[];
  /** Cặp id→nhãn, đúng thứ tự worker gửi; vắng khi id thiếu/trùng/sai dạng. */
  pairs?: OptionIdLabelPair[];
  /** Lý do `pairs` vắng (worker có gửi options) — dùng cho dòng "không auto vì …". */
  pairsProblem?: string;
}

/** Nhãn đọc được của một option: `title|label|summary` trước, `id` kỹ thuật sau (luật v1). */
function readableLabel(record: Record<string, unknown>): string | undefined {
  for (const key of ["title", "label", "summary", "id"]) {
    const value = record[key];
    if (typeof value === "string" && value.trim().length > 0) return value.trim();
  }
  return undefined;
}

function optionRecords(options: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(options)) return [];
  // Schema thật: mỗi phần tử LUÔN là object (autopilot-result-schema.ts) —
  // không có nhánh string (code-reviewer 260916 L1).
  return options.filter(
    (o): o is Record<string, unknown> => o !== null && typeof o === "object" && !Array.isArray(o),
  );
}

function idPairs(records: Array<Record<string, unknown>>): {
  pairs?: OptionIdLabelPair[];
  problem?: string;
} {
  if (records.length === 0) return { problem: "options rỗng" };
  const pairs: OptionIdLabelPair[] = [];
  for (const raw of records) {
    const id = raw.id;
    if (typeof id !== "string" || !OPTION_ID_PATTERN.test(id)) {
      return { problem: "options[].id thiếu hoặc sai dạng ^[a-z0-9-]{1,32}$" };
    }
    const label = readableLabel(raw) ?? id;
    pairs.push({ id, label, raw });
  }
  if (new Set(pairs.map((p) => p.id)).size !== pairs.length) {
    return { problem: "options[].id trùng nhau" };
  }
  return { pairs };
}

export function readNeedsDecisionOptions(options: unknown): NeedsDecisionOptions {
  const records = optionRecords(options);
  const labels: string[] = [];
  for (const record of records) {
    const label = readableLabel(record);
    if (label !== undefined) labels.push(label);
  }
  const { pairs, problem } = idPairs(records);
  return {
    // Trùng nhãn ⇒ v1 không phân biệt được người chọn cái nào; giữ lần đầu.
    v1Labels: [...new Set(labels)],
    ...(pairs !== undefined ? { pairs } : {}),
    ...(problem !== undefined ? { pairsProblem: problem } : {}),
  };
}
